import { createHttpClient } from '@adapters/HttpClient';
import type { HttpClient } from '@adapters/HttpClient/types';
import { apiClientWH } from '@shared/services/ApiClient';

// ========================================
// Adapter para obtener órdenes externas por tienda y canal
// Nota: Todas las llamadas a APIs externas deben ir en adapters
// ========================================

export interface ExternalOrderItemApi {
  product_id: string | number;
  variant_id?: string | number | null;
  name: string;
  sku?: string | null;
  qty: number;
  image?: string | null;
}

export interface ExternalOrderApi {
  external_id: string | number;
  ref: string;
  lines: ExternalOrderItemApi[];
  /** Si true, el pedido ya está en el almacén y no se puede volver a importar */
  imported?: boolean;
  order_id?: string | number | null;
}

/** Respuesta cruda del backend (nombres o tipos pueden variar) */
type ExternalOrderRaw = Record<string, unknown>;

/** ID interno del pedido en el almacén (si existe, ya está importado) */
function resolveWarehouseOrderId(raw: ExternalOrderRaw): string | number | null {
  const oid = raw.order_id ?? raw.orderId;
  if (oid == null || oid === '') return null;
  return oid as string | number;
}

function resolveImported(raw: ExternalOrderRaw): boolean {
  const v = raw.imported ?? raw.is_imported;
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  }
  const wid = resolveWarehouseOrderId(raw);
  if (wid != null && String(wid).trim() !== '') return true;
  return false;
}

function normalizeExternalOrder(raw: ExternalOrderRaw): ExternalOrderApi {
  const lines = (Array.isArray(raw.lines) ? raw.lines : []) as ExternalOrderItemApi[];
  const warehouseId = resolveWarehouseOrderId(raw);
  return {
    external_id: raw.external_id as string | number,
    ref: String(raw.ref ?? ''),
    lines,
    imported: resolveImported(raw),
    order_id: warehouseId,
  };
}

function normalizeExternalOrdersPayload(body: unknown): ExternalOrderApi[] {
  let list: unknown[] = [];
  if (Array.isArray(body)) {
    list = body;
  } else if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    if (Array.isArray(o.data)) {
      list = o.data;
    } else if (o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
      const inner = o.data as Record<string, unknown>;
      if (Array.isArray(inner.data)) {
        list = inner.data;
      } else if (Array.isArray(inner.orders)) {
        list = inner.orders;
      } else if (Array.isArray(inner.records)) {
        list = inner.records;
      } else if (Array.isArray(inner.items)) {
        list = inner.items;
      }
    } else if (Array.isArray(o.items)) {
      list = o.items;
    } else if (Array.isArray(o.orders)) {
      list = o.orders;
    } else if (Array.isArray(o.records)) {
      list = o.records;
    }
  }
  return list.map((item) => normalizeExternalOrder(item && typeof item === 'object' ? (item as ExternalOrderRaw) : {}));
}

/**
 * Regla única para UI + selección: si hay pedido en almacén (order_id) o imported explícito.
 * Prioriza order_id porque el backend a veces solo envía eso.
 */
export function isImportedExternalOrder(order: ExternalOrderApi): boolean {
  const oid = order.order_id;
  if (oid != null && String(oid).trim() !== '') return true;
  return order.imported === true;
}

export class OrdersExternalApiAdapter {
  private httpClient: HttpClient;

  constructor(httpClient?: HttpClient) {
    // Permitir inyección o crear por defecto
    this.httpClient = httpClient ?? createHttpClient(apiClientWH);
  }

  // Obtiene la lista completa de órdenes externas para una tienda/canal
  async getExternalOrders(params: { shopId: number; channelId: number }): Promise<ExternalOrderApi[]> {
    const { shopId, channelId } = params;

    // Estructurar parámetros de consulta
    const queryParams: Record<string, any> = {
      shop_id: shopId,
      channel_id: channelId,
    };

    // Mostrar loading global sólo en la primera carga (true aquí, el caller decide su UX)
    const response = await this.httpClient.get<unknown>('/orders/external', queryParams, {}, true);

    // Axios ya devuelve el body; puede ser array, { data: [] }, { data: { data: [] } }, etc.
    return normalizeExternalOrdersPayload(response);
  }
}
